// Quotes.tsx
import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import Tabs from "../../widgets/tabs";
import LoaderTrans from "../../widgets/loadertrans";
import Table from "../../widgets/table";
import ErrorCard from "../../widgets/error";
import Swal from "sweetalert2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";

import {
  ERROR_MESSAGE,
  NODATA_ERROR_MESSAGE,
} from "../../constants/dialog.constants";
import { BENCHMARK_QUOTES, DATE_FORMAT_UPDATED } from "../../constants/app.constants";
import {
  fetchBenchmarkQuotes,
  printBenchmarkQuote,
  quoteToJob,
} from "../quotes/quotes.utils";
import QuoteApprovalModal from "./quotesApproveModal";
import QuoteRejectModal from "./quotesRejectModal";
import { headerType } from "../../widgets/table";
import ChipTabs, { ChipTabItem } from "./chipFilterTabs";

interface ButtonProps {
  btnClassName: string;
  btnName: string;
  btnType?: string;
  btnColHeading?: string;
}

const tabData = [
  { label: "Overview", path: "Overview" },
  { label: "Client Connection", path: "ClientConnection" },
  { label: "Invoicing", path: "Invoicing" },
  { label: "Scheduling", path: "Scheduling" },
  { label: "Quotes", path: "Quotes" },
];

const colHeaders: headerType[] = [
  { label: "ID", style: { alignment: { verticalAlign: "middle" } } },
  { label: "Date", style: { alignment: { verticalAlign: "middle" } } },
  { label: "Deliver To", style: { alignment: { verticalAlign: "middle" } }, exlStyle: { width: 30 } },
  { label: "Type", style: { alignment: { verticalAlign: "middle" } }, exlStyle: { width: 18 } },
  {
    label: "Product",
    style: {
      alignment: {
        verticalAlign: "middle",
        color: "blue", // cell color
      },
    },
    headerColor: "black", // 👈 fixes iOS
    exlStyle: { width: 18 },
  },
  { label: "Yardage", style: { alignment: { textAlign: "left", verticalAlign: "middle" } }, exlStyle: { alignment: { horizontal: "left" }, width: 10 } },
  { label: "Total", style: { alignment: { textAlign: "right", verticalAlign: "middle" } }, exlStyle: { alignment: { horizontal: "right" }, width: 15 } },
];

const Quotes: React.FC = () => {
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [dateKey, setDateKey] = useState(0);
  const [dateError, setDateError] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [quoteId, setQuoteId] = useState(0);
  const [printQuoteFlag, setPrintQuoteFlag] = useState(false);

  const buttonsClass: ButtonProps[] = [
    { btnClassName: "tn btn-sm requestChangeBtn", btnName: "Print", btnType: "Button" },
    { btnClassName: "btn btn-sm approveBtn", btnName: "Approve", btnType: "Button" },
    { btnClassName: "btn btn-sm rejectBtn cancelPwdBtn", btnName: "Reject", btnType: "Button" },
  ];

  // -------------------- DATE VALIDATION --------------------
  const isValidDateRange = (): boolean => {
    if ((startDate && !startDate.isValid()) || (endDate && !endDate.isValid())) return false;
    if (startDate && endDate) return !startDate.isAfter(endDate);
    return true;
  };

  // -------------------- FETCH QUOTES --------------------
  const {
    isLoading,
    isFetching,
    error,
    data,
    refetch,
  } = useQuery({
    queryKey: [
      BENCHMARK_QUOTES,
      startDate?.isValid() ? startDate.toISOString() : null,
      endDate?.isValid() ? endDate.toISOString() : null,
    ],
    queryFn: () =>
      fetchBenchmarkQuotes(
        localStorage.getItem("ID") ?? "",
        startDate?.isValid() ? startDate.toDate() : undefined,
        endDate?.isValid() ? endDate.toDate() : undefined
      ),
    enabled: isValidDateRange(),
    refetchOnWindowFocus: false,
  });

  // -------------------- PRINT QUOTE --------------------
  const {
    isLoading: isLoadingPrint,
    data: dataPrint,
    isFetching: isFetchingPrint,
  } = useQuery({
    queryKey: ["BENCHMARK_QUOTES_PRINT", quoteId],
    queryFn: () => printBenchmarkQuote(localStorage.getItem("ID"), quoteId),
    enabled: printQuoteFlag,
    refetchOnWindowFocus: false,
  });

  const printQuote = useCallback(
    (id: number) => {
      setQuoteId(id);
      setPrintQuoteFlag(true);
    },
    []
  );

  // -------------------- MODALS --------------------
  const onClickApproveQuote = useCallback((id: number) => {
    setPrintQuoteFlag(false);
    setIsModalOpen(true);
    setQuoteId(id);
  }, []);

  const onClickRejectQuote = useCallback((id: number) => {
    setPrintQuoteFlag(false);
    setIsRejectModalOpen(true);
    setQuoteId(id);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setQuoteId(0);
  }, []);

  const handleRejectModalClose = useCallback(() => {
    setIsRejectModalOpen(false);
    setQuoteId(0);
  }, []);

  // -------------------- DATE EFFECT --------------------
  useEffect(() => {
    if (startDate?.isValid() && endDate?.isValid() && startDate.isAfter(endDate)) {
      setDateError("Start date cannot be greater than end date.");
      return;
    }

    setDateError("");
    if (isValidDateRange()) {
      refetch();
    }
  }, [startDate, endDate, refetch]);

  const onResetButton = () => {
    setStartDate(null);
    setEndDate(null);
    setDateKey(prev => prev + 1); // force remount of DatePickers
    setDateError("");
    refetch();
  };

  // -------------------- OPEN PRINT --------------------
  useEffect(() => {
    if (!isLoadingPrint && dataPrint && !isFetchingPrint && printQuoteFlag) {
      window.open(dataPrint, "_blank");
      setPrintQuoteFlag(false);
    }
  }, [isLoadingPrint, isFetchingPrint, dataPrint, printQuoteFlag]);


  const getStatus = (row: unknown): string => {
    if (Array.isArray(row)) {
      // For array rows, find status by checking common status values
      // Status is typically one of: Submitted, Expired, Approved, Rejected
      const statusValue = row.find((item) => {
        if (!item) return false;
        const str = item.toString().toLowerCase();
        return ["submitted", "expired", "approved", "rejected"].includes(str);
      });
      return statusValue ? statusValue.toString() : "";
    } else if (row && typeof row === "object") {
      // For object rows, check multiple possible property names
      const r = row as Record<string, unknown>;
      const statusKey = Object.keys(r).find((key) =>
        key.toLowerCase().includes("status")
      );
      return statusKey ? ((r[statusKey] ?? "") as string).toString() : "";
    }
    return "";
  };

  const [active, setActive] = useState("all");

  const statusCounts = useMemo(() => {
    if (!data) return { noStatus: 0, submitted: 0, expired: 0, approved: 0, rejected: 0 };
    const counts = { noStatus: 0, submitted: 0, expired: 0, approved: 0, rejected: 0 };
    data.forEach((row: unknown) => {
      const status = getStatus(row).toLowerCase();
      if (!status) counts.noStatus++;
      else if (status === "submitted") counts.submitted++;
      else if (status === "expired") counts.expired++;
      else if (status === "approved") counts.approved++;
      else if (status === "rejected") counts.rejected++;
    });
    return counts;
  }, [data, getStatus]);

  const tabItems: ChipTabItem[] = [
    { key: "all", label: `All (${data?.length ?? 0})` },
    { key: "no-status", label: `No Status (${statusCounts.noStatus})` },
    { key: "submitted", label: `Submitted (${statusCounts.submitted})` },
    { key: "expired", label: `Expired (${statusCounts.expired})` },
    { key: "approved", label: `Approved (${statusCounts.approved})` },
    { key: "rejected", label: `Rejected (${statusCounts.rejected})` }
  ];

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (active === "all") return data;

    const keyToStatus: Record<string, string> = {
      "no-status": "",
      submitted: "Submitted",
      expired: "Expired",
      approved: "Approved",
      rejected: "Rejected",
    };

    const statusFilter = keyToStatus[active] ?? active;

    return data.filter((row: unknown) => {
      const status = getStatus(row);
      return (status || "").toString().toLowerCase() === statusFilter.toLowerCase();
    });
  }, [data, active, getStatus]);


  const groupedData = useMemo(() => {
    if (!data || active !== "all") return null;

    const groups: Record<string, unknown[]> = {};
    const statusOrder = ["", "Submitted", "Expired", "Approved", "Rejected"];

    statusOrder.forEach(status => {
      groups[status] = [];
    });

    data.forEach((row: unknown) => {
      const rawStatus = getStatus(row).trim();
      const normalizedStatus = rawStatus.charAt(0).toUpperCase() + rawStatus.slice(1).toLowerCase();

      if (groups[normalizedStatus]) {
        groups[normalizedStatus].push(row);
      } else {
        if (!groups[normalizedStatus]) {
          groups[normalizedStatus] = [];
        }
        groups[normalizedStatus].push(row);
      }
    });

    return groups;
  }, [data, active, getStatus]);

  return (
    <div className="container-fluid">
      <div className="body-content">
        <Tabs tabs={tabData} activeTabIndex={4} />

        <div className="d-flex flex-column propertiesContent quotesContent ">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="d-flex align-items-center gap-3 m-flex-colum justify-content-between">
              <ChipTabs tabs={tabItems} activeKey={active} onChange={setActive} />

              <div className="d-flex flex-column mw-100">
                <div className="date-filter-container">
                  <div className="d-flex gap-2">
                    <DatePicker
                      key={dateKey + "-start"}
                      label="Start Date"
                      value={startDate}
                      onChange={(newValue) => setStartDate(newValue)}
                      format={DATE_FORMAT_UPDATED}
                      disableFuture
                      slotProps={{ textField: { size: "small", className: "date-input-field" } }}
                    />
                    <DatePicker
                      key={dateKey + "-end"}
                      label="End Date"
                      value={endDate}
                      onChange={(newValue) => setEndDate(newValue)}
                      format={DATE_FORMAT_UPDATED}
                      disableFuture
                      slotProps={{ textField: { size: "small", className: "date-input-field" } }}
                    />
                  </div>

                  <button className="btn btn-sm resetBtn" onClick={onResetButton}>
                    Reset
                  </button>
                </div>
                <div className="d-flex justify-content-start">
                  {dateError && <p className="text-danger mb-0">{dateError}</p>}</div>
              </div>
            </div>

          </LocalizationProvider>

          {(isLoading || isFetching || isLoadingPrint || isFetchingPrint) && <LoaderTrans msg="Loading" />}

          {(error) && <ErrorCard msg={ERROR_MESSAGE} />}

          {!error && !isLoading && !isFetching && (!filteredData || filteredData.length === 0) && <ErrorCard msg={NODATA_ERROR_MESSAGE} />}

          {!isLoading && !error && filteredData && filteredData.length > 0 && (
            <>
              {active === "all" && groupedData ? (
                ["", "Submitted", "Expired", "Approved", "Rejected"].map((status) => {
                  const rows = groupedData[status] || [];
                  const displayStatus = status || "No Status";
                  return rows.length > 0 ? (
                    <div key={status} style={{ marginBottom: "20px" }}>
                      {/* <h5 style={{ marginBottom: "10px", fontWeight: "600" }}>{status} ({rows.length})</h5> */}
                      <Table
                        id={status ? `quotes-${status.toLowerCase()}` : "quotes-empty-status"}
                        classNameTable="tblHeightQuotes quotes-table"
                        tableHeading={`${displayStatus} (${rows.length})`}
                        headers={colHeaders}
                        data={rows as string[][]}
                        exportExcel
                        fileName={`Quotes-${displayStatus.replace(/\s+/g, "-")}`}
                        buttonsClass={buttonsClass}
                        onClickButton1={printQuote}
                        onClickButton2={onClickApproveQuote}
                        onClickButton3={onClickRejectQuote}
                      />
                    </div>
                  ) : null;
                })
              ) : (
                <Table
                  id="quotes"
                  classNameTable="tblHeightQuotes"
                  tableHeading={
                    active !== "all"
                      ? `${active === "no-status" ? "No Status" : active.charAt(0).toUpperCase() + active.slice(1)} (${filteredData.length})`
                      : undefined
                  }
                  headers={colHeaders}
                  data={filteredData as string[][]}
                  exportExcel
                  fileName="Quotes"
                  buttonsClass={buttonsClass}
                  onClickButton1={printQuote}
                  onClickButton2={onClickApproveQuote}
                  onClickButton3={onClickRejectQuote}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* <QuoteApprovalModal isOpen={isModalOpen} onClose={handleModalClose} quoteId={quoteId} /> */}
      <QuoteApprovalModal
        isOpen={isModalOpen}
        quoteId={quoteId}
        onClose={() => {
          handleModalClose();
          refetch(); // 👈 force getQuotes API call
        }}
      />

      <QuoteRejectModal
        isOpen={isRejectModalOpen}
        quoteId={quoteId}
        onClose={() => {
          handleRejectModalClose();
          refetch(); // 👈 refresh quotes after reject
        }}
      />

    </div>
  );
};

export default Quotes;
